import { createFileRoute, Outlet, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { OfflineBanner } from "@/lib/offline";
import { BoldMoveNudge } from "@/components/BoldMoveNudge";
import { FirstRun } from "@/components/FirstRun";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Lock,
  Download,
  ShieldCheck,
  Sun,
  Lightbulb,
  CalendarDays,
  NotebookPen,
  Users,
  History,
  Settings,
  ChevronDown,
  ChevronUp,
  Menu,
  LayoutDashboard,
  HelpCircle,
  Timer,

} from "lucide-react";
import { getSupabase } from "@/lib/supabase-lazy";
import {
  getPinStatus,
  listBusinesses,
  createBusiness,
  renameBusiness,
  deleteBusiness,
} from "@/lib/momentum.functions";
import type { Business } from "@/lib/momentum-types";
import { BusinessContext } from "@/components/business-context";
import { PinLock } from "@/components/PinLock";
import { getAutoLockMinutes } from "@/lib/auto-lock";

import { pickQuote } from "@/lib/quotes";
import { getAccount, exportData } from "@/lib/account.functions";
import { planOf } from "@/lib/plans";
import { BetaBanner } from "@/components/BetaBanner";
import { DemoBanner } from "@/components/DemoBanner";
import { FeedbackLauncher } from "@/components/FeedbackLauncher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { WorldClocks } from "@/components/WorldClocks";
import { ThemeSwitcher, hydrateTheme } from "@/components/ThemeSwitcher";
import { HelpGuide } from "@/components/HelpGuide";
import { PrefsContext } from "@/components/prefs-context";
import { getPrefs, saveHiddenSections } from "@/lib/prefs.functions";
import type { SectionKey } from "@/lib/sections";
import { isThemeId } from "@/lib/themes";




export const Route = createFileRoute("/_authenticated/app")({
  component: AppShell,
});

const NAV = [
  { key: "dashboard", to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "today", to: "/app/today", label: "Today", icon: Sun },
  { key: "focus", to: "/app/focus", label: "Focus", icon: Timer },
  { key: "ideas", to: "/app/ideas", label: "Ideas", icon: Lightbulb },
  { key: "appointments", to: "/app/appointments", label: "Calendar", icon: CalendarDays },
  { key: "journal", to: "/app/journal", label: "Journal", icon: NotebookPen },
  { key: "contacts", to: "/app/contacts", label: "Contacts", icon: Users },
  { key: "history", to: "/app/history", label: "History", icon: History },
] as const;

const SETTINGS_NAV = { to: "/app/settings", label: "Settings", icon: Settings } as const;

const UNLOCK_KEY = "momentum.unlocked";


function AppShell() {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [checkedSession, setCheckedSession] = useState(false);
  const [activeId, setActiveIdState] = useState<string | null>(null);
  const [quote] = useState(() => pickQuote());
  const [dialog, setDialog] = useState<{ mode: "new" | "rename"; id?: string; name: string } | null>(
    null,
  );
  const [navExpanded, setNavExpanded] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem("momentum.navExpanded") !== "false";
  });

  const [hidden, setHiddenState] = useState<SectionKey[]>([]);
  const [helpOpen, setHelpOpen] = useState(false);
  // Onboarding is only for accounts that have never created a business.
  const [firstRun, setFirstRun] = useState(false);

  const prefs = useQuery({ queryKey: ["prefs"], queryFn: () => getPrefs() });

  // Theme and menu layout follow the account, not the device.
  useEffect(() => {
    if (!prefs.data) return;
    if (isThemeId(prefs.data.theme)) hydrateTheme(prefs.data.theme);
    setHiddenState((prefs.data.hiddenSections ?? []) as SectionKey[]);
  }, [prefs.data]);

  const setHidden = (next: SectionKey[]) => {
    setHiddenState(next);
    void saveHiddenSections({ data: { hidden: next } }).catch(() => {
      toast.error("Couldn't save your menu layout");
    });
  };

  const visibleNav = NAV.filter((item) => !hidden.includes(item.key));

  const pinStatus = useQuery({ queryKey: ["pin-status"], queryFn: () => getPinStatus() });

  const account = useQuery({ queryKey: ["account"], queryFn: () => getAccount() });
  const plan = planOf(account.data?.plan.code);
  const runExport = useServerFn(exportData);

  const downloadExport = async () => {
    try {
      const payload = await runExport(undefined as never);
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `momentum-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    }
  };
  const businessesQuery = useQuery({ queryKey: ["businesses"], queryFn: () => listBusinesses() });
  const businesses = (businessesQuery.data ?? []) as Business[];

  const create = useServerFn(createBusiness);
  const rename = useServerFn(renameBusiness);
  const remove = useServerFn(deleteBusiness);

  useEffect(() => {
    setUnlocked(sessionStorage.getItem(UNLOCK_KEY) === "1");
    setCheckedSession(true);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("momentum.activeBusiness");
    if (stored) setActiveIdState(stored);
  }, []);

  const setActiveId = (id: string) => {
    setActiveIdState(id);
    localStorage.setItem("momentum.activeBusiness", id);
  };

  // Offer onboarding once per session; never trap the user back into it.
  const onboardingOffered = useRef(false);
  useEffect(() => {
    if (!businessesQuery.isSuccess) return;
    if (businesses.length === 0 && !onboardingOffered.current) {
      onboardingOffered.current = true;
      setFirstRun(true);
    }
  }, [businessesQuery.isSuccess, businesses.length]);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onSettings = pathname.startsWith("/app/settings");

  const active = useMemo(
    () => businesses.find((b) => b.id === activeId) ?? businesses[0] ?? null,
    [businesses, activeId],
  );

  const unlock = () => {
    sessionStorage.setItem(UNLOCK_KEY, "1");
    setUnlocked(true);
  };

  // Inactivity auto-lock: after a quiet stretch, drop back to the PIN screen.
  useEffect(() => {
    if (!unlocked) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let minutes = getAutoLockMinutes();

    const lockNow = () => {
      sessionStorage.removeItem(UNLOCK_KEY);
      setUnlocked(false);
    };

    const arm = () => {
      if (timer) clearTimeout(timer);
      if (minutes > 0) timer = setTimeout(lockNow, minutes * 60_000);
    };

    const onActivity = () => arm();
    const onPrefChange = () => {
      minutes = getAutoLockMinutes();
      arm();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") arm();
    };

    const events = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("momentum:auto-lock-changed", onPrefChange);
    arm();

    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("momentum:auto-lock-changed", onPrefChange);
    };
  }, [unlocked]);

  const signOut = async () => {
    sessionStorage.removeItem(UNLOCK_KEY);
    await (await getSupabase()).auth.signOut();
    void navigate({ to: "/auth" });
  };


  const submitDialog = async () => {
    if (!dialog) return;
    const name = dialog.name.trim();
    if (!name) return;
    try {
      if (dialog.mode === "new") {
        const row = await create({ data: { name } });
        if (row?.id) setActiveId(row.id as string);
        toast.success(`${name} added`);
      } else if (dialog.id) {
        await rename({ data: { id: dialog.id, name } });
      }
      setDialog(null);
      await businessesQuery.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save");
    }
  };

  const removeBusiness = async (b: Business) => {
    if (
      !confirm(
        `Move ${b.name} to Trash? Nothing is erased — you can restore it from Settings → Trash.`,
      )
    )
      return;
    await remove({ data: { id: b.id } });
    if (activeId === b.id) localStorage.removeItem("momentum.activeBusiness");
    setActiveIdState(null);
    await businessesQuery.refetch();
  };

  if (!checkedSession || pinStatus.isLoading || account.isLoading) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">…</div>;
  }

  // The public demo is shared, so it never sits behind a PIN — one visitor
  // setting one would lock everyone else out until the nightly reset.
  if (!unlocked && !account.data?.isDemo) {
    return <PinLock hasPin={Boolean(pinStatus.data?.hasPin)} onUnlock={unlock} />;
  }

  return (
    <PrefsContext.Provider
      value={{
        hidden,
        setHidden,
        openHelp: () => setHelpOpen(true),
      }}
    >
    <BusinessContext.Provider
      value={{ businesses, active, setActiveId, refresh: () => void businessesQuery.refetch() }}
    >
      <div className="flex min-h-screen">
        <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-sidebar md:flex">
          <div className="flex items-center justify-between border-b border-border px-5 py-5">
            <Link to="/" className="micro-label text-foreground">
              The Founders App
            </Link>
            <div className="flex gap-0.5">
              <button
                onClick={() => {
                  sessionStorage.removeItem(UNLOCK_KEY);
                  setUnlocked(false);
                }}
                className="rounded-sm p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label="Lock"
              >
                <Lock className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => void signOut()}
                className="rounded-sm p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label="Sign out"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-5">
            <p className="micro-label px-2">Businesses</p>
            <div className="mt-3 space-y-px">
              {businesses.map((b, i) => (
                <div key={b.id} className="group relative flex items-center">
                  <button
                    onClick={() => setActiveId(b.id)}
                    className={cn(
                      "flex flex-1 items-baseline gap-3 truncate rounded-sm px-2 py-2.5 text-left text-sm transition-colors",
                      active?.id === b.id
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                    )}
                  >
                    <span className="text-[0.625rem] tabular-nums text-muted-foreground/50">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="notranslate truncate">{b.name}</span>
                  </button>
                  <div className="absolute right-1 flex opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      onClick={() => setDialog({ mode: "rename", id: b.id, name: b.name })}
                      className="rounded-sm p-1.5 text-muted-foreground hover:text-foreground"
                      translate="no"
                      aria-label={`Rename ${b.name}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => void removeBusiness(b)}
                      className="rounded-sm p-1.5 text-muted-foreground hover:text-destructive"
                      translate="no"
                      aria-label={`Delete ${b.name}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setDialog({ mode: "new", name: "" })}
              className="mt-4 flex w-full items-center gap-2 rounded-sm border border-dashed border-border px-3 py-2.5 text-left text-xs text-muted-foreground transition-colors hover:border-hairline hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" /> Add a business
            </button>
          </nav>

          <div className="space-y-4 border-t border-border px-5 py-5">
            <div className="flex items-center justify-between gap-2">
              <Link
                to="/pricing"
                className="micro-label text-muted-foreground transition-colors hover:text-foreground"
              >
                {plan.name} plan
              </Link>
              <div className="flex gap-0.5">
                {plan.export && (
                  <button
                    onClick={() => void downloadExport()}
                    className="rounded-sm p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    aria-label="Export everything"
                    title="Export everything"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                )}
                {account.data?.isAdmin && (
                  <Link
                    to="/admin"
                    className="rounded-sm p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    aria-label="Control room"
                    title="Control room"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs leading-relaxed text-muted-foreground">{quote.text}</p>
              <p className="mt-2 text-[0.625rem] tracking-[0.2em] text-muted-foreground/50 uppercase">
                {quote.author}
              </p>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-4 sm:flex-nowrap sm:px-8">
              <div className="order-1 min-w-0 sm:order-none">
                <p className="micro-label">
                  {new Date().toLocaleDateString(undefined, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
                </p>
                <h1 className="notranslate mt-1 hidden truncate text-xl font-medium sm:block">
                  {active?.name ?? "No business yet"}
                </h1>
              </div>
              <h1 className="notranslate order-3 w-full min-w-0 truncate text-xl font-medium sm:hidden">
                {active?.name ?? "No business yet"}
              </h1>
              <WorldClocks className="hidden lg:flex" />
              <ThemeSwitcher className="hidden shrink-0 md:block" />
              <div className="order-2 flex shrink-0 items-center gap-1 md:hidden">
                <ThemeSwitcher />
                <button
                  onClick={() => setHelpOpen(true)}
                  className="rounded-sm border border-border p-2 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="How The Founders App works"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
                {account.data?.isAdmin && (
                  <Link
                    to="/admin"
                    className="rounded-sm border border-border p-2 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Control room"
                  >
                    <ShieldCheck className="h-4 w-4" />
                  </Link>
                )}
                <button
                  onClick={() => {
                    sessionStorage.removeItem(UNLOCK_KEY);
                    setUnlocked(false);
                  }}
                  className="rounded-sm border border-border p-2 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="Lock"
                >
                  <Lock className="h-4 w-4" />
                </button>
                <button
                  onClick={() => void signOut()}
                  className="rounded-sm border border-border p-2 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDialog({ mode: "new", name: "" })}
                  className="rounded-sm border border-border p-2 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="Add a business"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <WorldClocks className="justify-end border-t border-border/60 px-5 py-2 lg:hidden" />

            {businesses.length > 0 && (
              <>
                {/* Mobile: compact dropdown so every business is reachable without horizontal scroll */}
                <div className="border-t border-border/60 px-4 py-2 md:hidden">
                  <Select value={active?.id ?? ""} onValueChange={setActiveId}>
                    <SelectTrigger className="h-8 w-full text-xs">
                      <SelectValue placeholder="Choose a business" />
                    </SelectTrigger>
                    <SelectContent>
                      {businesses.map((b) => (
                        <SelectItem key={b.id} value={b.id} className="notranslate text-xs">
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

              </>
            )}

            {active && (
              <>
                {/* Desktop / tablet: horizontal tabs */}
                <nav className="scroll-fade hidden items-center gap-1 overflow-x-auto px-3 md:flex sm:gap-2 sm:px-6">
                  {visibleNav.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="group -mb-px flex shrink-0 items-center gap-2 border-b-2 border-transparent px-2 py-3 text-[0.7rem] font-semibold tracking-[0.14em] text-muted-foreground uppercase transition-colors hover:text-foreground"
                      activeProps={{ className: "text-foreground border-primary" }}
                    >
                      <item.icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                  <Link
                    to={SETTINGS_NAV.to}
                    className="-mb-px ml-auto flex shrink-0 items-center gap-2 border-b-2 border-transparent px-2 py-3 text-[0.7rem] font-semibold tracking-[0.14em] text-muted-foreground uppercase transition-colors hover:text-foreground"
                    activeProps={{ className: "text-foreground border-primary" }}
                    aria-label="Settings"
                  >
                    <SETTINGS_NAV.icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="hidden lg:inline">{SETTINGS_NAV.label}</span>
                  </Link>
                  <button
                    onClick={() => setHelpOpen(true)}
                    className="group -mb-px flex shrink-0 items-center gap-2 border-b-2 border-transparent px-2 py-3 text-[0.7rem] font-semibold tracking-[0.14em] text-muted-foreground uppercase transition-colors hover:text-foreground"
                    aria-label="How The Founders App works"
                  >
                    <HelpCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="hidden lg:inline">Help</span>
                  </button>
                </nav>

                {/* Mobile: collapsible nav strip */}
                <div className="border-t border-border/60 md:hidden">
                  <button
                    onClick={() => {
                      setNavExpanded((v) => {
                        const next = !v;
                        localStorage.setItem("momentum.navExpanded", String(next));
                        return next;
                      });
                    }}
                    className="flex w-full items-center justify-between px-4 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={navExpanded ? "Collapse menu" : "Expand menu"}
                    aria-expanded={navExpanded}
                  >
                    <span className="flex items-center gap-2">
                      <Menu className="h-3.5 w-3.5" aria-hidden="true" />
                      <span className="uppercase tracking-wider">Menu</span>
                    </span>
                    {navExpanded ? (
                      <ChevronUp className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />
                    )}
                  </button>

                  {navExpanded && (
                    <nav className="grid grid-cols-4 gap-1 border-t border-border/60 px-3 py-3">
                      {visibleNav.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="group flex flex-col items-center justify-center gap-1.5 rounded-sm bg-accent/30 px-1 py-3 text-[0.65rem] font-medium text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
                          activeProps={{ className: "bg-accent text-accent-foreground ring-1 ring-primary/40 hover:bg-accent" }}
                        >
                          <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                          <span>{item.label}</span>
                        </Link>
                      ))}
                      <Link
                        to={SETTINGS_NAV.to}
                        className="group flex flex-col items-center justify-center gap-1.5 rounded-sm bg-accent/30 px-1 py-3 text-[0.65rem] font-medium text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
                        activeProps={{ className: "bg-accent text-accent-foreground ring-1 ring-primary/40 hover:bg-accent" }}
                        aria-label="Settings"
                      >
                        <SETTINGS_NAV.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <span>{SETTINGS_NAV.label}</span>
                      </Link>
                    </nav>
                  )}
                </div>
              </>
            )}
          </header>

          <main id="main" className="flex-1 px-5 py-8 sm:px-8 sm:py-10">
            <div className="mx-auto max-w-3xl">
              <OfflineBanner />
              {account.data?.isDemo ? <DemoBanner /> : <BetaBanner onSettings={onSettings} />}
              {active && !firstRun && <BoldMoveNudge />}
              {onSettings ? (
                <Outlet />
              ) : firstRun ? (
                <FirstRun
                  onCreated={(id) => setActiveId(id)}
                  onDone={async () => {
                    setFirstRun(false);
                    await businessesQuery.refetch();
                    void navigate({ to: "/app/today" });
                  }}
                />
              ) : active ? (
                <Outlet />
              ) : (
                <div className="vault-panel px-8 py-14 text-center">
                  <p className="micro-label">No business yet</p>
                  <h2 className="mt-4 text-2xl font-medium">Start with one business</h2>

                  <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
                    Add the first one and today's move is waiting the moment you land.
                  </p>
                  <Button className="sheen mt-8" onClick={() => setDialog({ mode: "new", name: "" })}>
                    <Plus className="mr-2 h-4 w-4" /> Add a business
                  </Button>
                  <p className="mt-6 text-xs text-muted-foreground">
                    Deleted one by mistake?{" "}
                    <Link
                      to="/app/settings"
                      className="text-foreground underline-offset-4 hover:underline"
                    >
                      Restore it from Trash
                    </Link>
                  </p>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      <Dialog open={Boolean(dialog)} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "new" ? "Add a business" : "Rename business"}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={dialog?.name ?? ""}
            placeholder="e.g. Fern Studio"
            onChange={(e) => setDialog((d) => (d ? { ...d, name: e.target.value } : d))}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submitDialog();
            }}
          />
          <DialogFooter>
            <Button onClick={() => void submitDialog()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <HelpGuide
        open={helpOpen}
        onOpenChange={(open) => setHelpOpen(open)}
      />
      <FeedbackLauncher page={pathname} />
    </BusinessContext.Provider>
    </PrefsContext.Provider>

  );
}
